import { inject, Injectable } from '@angular/core';
import { InsightApiService } from '@marmon/frontend/krista';
import {
	Insight,
	isPendingInsight,
	isPollableInsight,
} from '@marmon/shared/types/krista';
import { map, Observable, of, switchMap } from 'rxjs';
import { followInsight } from './insight-follow';

export { insightPollWaitMillis } from './insight-follow';

/**
 * Submits a driver report and follows it until Krista finishes.
 * The insights API returns immediately. A pending insight is polled
 * no sooner than `nextPollAt`, which the server uses as a lease.
 *
 * `load` only reads the cache. A pending insight that already has a
 * Krista event id is polled again. A completed insight is returned as-is.
 */
@Injectable({ providedIn: 'root' })
export class DriverReportInsightService {
	private readonly insights = inject(InsightApiService);

	generate(
		analysisId: string,
		calculationId: string,
		targetId: string,
	): Observable<Insight> {
		return this.insights
			.generateDriverReport(analysisId, calculationId, targetId)
			.pipe(
				map((response) => response.insight),
				switchMap((insight) =>
					insight.status === 'pending'
						? this.follow(analysisId, calculationId, insight)
						: of(insight),
				),
			);
	}

	load(
		analysisId: string,
		calculationId: string,
		targetId: string,
	): Observable<Insight | null> {
		return this.insights
			.lookupDriverReport(analysisId, calculationId, targetId)
			.pipe(
				switchMap(({ insight }) => {
					if (!insight || !isPendingInsight(insight)) {
						return of(insight);
					}
					if (!isPollableInsight(insight)) return of(null);
					return this.follow(analysisId, calculationId, insight);
				}),
			);
	}

	private follow(
		analysisId: string,
		calculationId: string,
		start: Insight,
	): Observable<Insight> {
		return followInsight(
			(insightId) =>
				this.insights.poll(analysisId, calculationId, insightId),
			start,
		);
	}
}
